import { useMemo, useState } from 'react';
import {
  getWorkspaceId,
  useAppSelector,
  useLazyGetAllGroupsQuery,
} from '@zonofi/zono-money-store';

export type DetailPickerType = 'lifecycle' | 'segments' | 'routes';

type CustomerGroup = { id: string; name: string; emojiId?: string };
type GroupNode = {
  id: string;
  name: string;
  customerGroups?: CustomerGroup[];
  subFolders?: GroupNode[];
};

export type ApiGroupResponse = {
  groupType: 'ROUTE' | 'SEGMENT' | 'CUSTOMER_LIFE_CYCLE';
  folders: GroupNode[];
  rootCustomerGroups: CustomerGroup[];
};

const getLabel = (item: CustomerGroup) =>
  `${item?.emojiId ?? ''} ${item?.name ?? ''}`.trim();

const walkGroupNodes = (
  nodes: GroupNode[] = [],
  values: CustomerGroup[] = []
): CustomerGroup[] => {
  nodes.forEach((node) => {
    (node.customerGroups ?? []).forEach((group) => values.push(group));
    if (node.subFolders?.length) walkGroupNodes(node.subFolders, values);
  });
  return values;
};

export const useCompanyGroupPicker = () => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [getCustomerGroupsApi] = useLazyGetAllGroupsQuery();
  const [activePicker, setActivePicker] = useState<DetailPickerType | null>(
    null
  );
  const [searchText, setSearchText] = useState('');
  const [groupMap, setGroupMap] = useState<
    Record<DetailPickerType, ApiGroupResponse | null>
  >({
    lifecycle: null,
    segments: null,
    routes: null,
  });
  const [selectedIds, setSelectedIds] = useState<
    Record<DetailPickerType, string[]>
  >({
    lifecycle: [],
    segments: [],
    routes: [],
  });

  const idLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    (Object.values(groupMap).filter(Boolean) as ApiGroupResponse[]).forEach(
      (group) => {
        [...group.rootCustomerGroups, ...walkGroupNodes(group.folders)].forEach(
          (item) => {
            map.set(item.id, getLabel(item));
          }
        );
      }
    );
    return map;
  }, [groupMap]);

  const selectedLabels = (type: DetailPickerType) =>
    selectedIds[type].map((id) => idLabelMap.get(id) || '').filter(Boolean);

  const openPicker = async (type: DetailPickerType) => {
    if (!sellerWorkspaceId) return;
    setActivePicker(type);
    setSearchText('');
    const response = (await getCustomerGroupsApi({
      sellerWorkspaceId,
      customerGroupTypes: ['ROUTE', 'SEGMENT', 'CUSTOMER_LIFE_CYCLE'],
    }).unwrap()) as ApiGroupResponse[];

    const byType = (groupType: ApiGroupResponse['groupType']) =>
      response.find((item) => item.groupType === groupType) ?? null;

    const nextMap = {
      routes: byType('ROUTE'),
      segments: byType('SEGMENT'),
      lifecycle: byType('CUSTOMER_LIFE_CYCLE'),
    };
    setGroupMap(nextMap);
    setSelectedIds((prev) => ({
      lifecycle:
        prev.lifecycle.length > 0
          ? prev.lifecycle
          : [
              ...(nextMap.lifecycle?.rootCustomerGroups ?? []),
              ...walkGroupNodes(nextMap.lifecycle?.folders),
            ]
              .slice(0, 1)
              .map((item) => item.id),
      segments:
        prev.segments.length > 0
          ? prev.segments
          : [
              ...(nextMap.segments?.rootCustomerGroups ?? []),
              ...walkGroupNodes(nextMap.segments?.folders),
            ]
              .slice(0, 2)
              .map((item) => item.id),
      routes:
        prev.routes.length > 0
          ? prev.routes
          : [
              ...(nextMap.routes?.rootCustomerGroups ?? []),
              ...walkGroupNodes(nextMap.routes?.folders),
            ]
              .slice(0, 1)
              .map((item) => item.id),
    }));
  };

  return {
    activePicker,
    setActivePicker,
    searchText,
    setSearchText,
    groupMap,
    selectedIds,
    setSelectedIds,
    selectedLabels,
    openPicker,
  };
};
